import { useState, useCallback } from 'react'
import EasyCropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'

interface ImageCropDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  imageSrc: string
  onCropComplete: (croppedImage: string) => void
  shape?: 'rectangle' | 'round'
  aspectRatio?: number
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', reject)
    img.setAttribute('crossOrigin', 'anonymous')
    img.src = url
  })
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  )
  return canvas.toDataURL('image/webp', 0.9)
}

const ImageCropDialog = ({
  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  shape = 'rectangle',
  aspectRatio,
}: ImageCropDialogProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const onCropCompleteCallback = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCrop({ x: 0, y: 0 })
      setZoom(1)
    }
    onOpenChange(next)
  }

  const handleApply = async () => {
    if (!croppedAreaPixels) return
    try {
      const dataUrl = await getCroppedImg(imageSrc, croppedAreaPixels)
      onCropComplete(dataUrl)
      onOpenChange(false)
    } catch (e) {
      console.error('Error cropping image:', e)
      toast.error('Failed to crop image. Please try again.')
    }
  }

  const aspect = shape === 'round' ? 1 : (aspectRatio ?? undefined)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md w-full p-0 overflow-hidden bg-white">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle
            className="text-gray-900"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}
          >
            Adjust Image
          </DialogTitle>
        </DialogHeader>

        <div className="relative h-72 w-full bg-gray-900 mx-0 mt-2">
          <EasyCropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={0.95}
            aspect={aspect}
            objectFit="contain"
            cropShape={shape === 'round' ? 'round' : 'rect'}
            showGrid={shape !== 'round'}
            restrictPosition={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <span
              className="text-sm text-gray-500 w-10"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Zoom
            </span>
            <input
              type="range"
              min={0.95}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-gray-800"
            />
          </div>
        </div>

        <DialogFooter className="p-4 pt-0 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-gray-300 text-gray-700"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="lime"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImageCropDialog
